'use client';

import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons';
import { ChangeEvent, useEffect } from 'react';
import { useStore } from 'zustand';

import { CreateAccountForm } from './register-form.component';

import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.component';
import { useLoading } from '@/hooks/use-loading.hook';
import { cpfValidator } from '@/utils/validators';
import { generateRegistrationCookies } from '@/cookies/registration.cookies';
import { StepKeys } from '@/app/(main)/_components/sign-in-modal/_types/header-auth-modal-step-keys.enum';
import { isPendingEmailValidation, login } from '@/services/auth/login.service';
import { formatISO8601toBRDate } from '@/utils/formatters/format-iso-8601-to-br-date';
import { resolveAuthenticatedUser } from '@/store/auth.store';
import { setLoginDocumentNumber } from '@/store/login.store';
import { modalsOpeningStore, setOpenSignInModal } from '@/store/modal';
import { useFocus } from '@/hooks/use-focus.hook';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';

const loginFormSchema = z.object({
  documentNumber: z.custom<string>(cpfValidator, {
    message: 'Informe seu CPF.',
  }),
  password: z.string().min(1, {
    message: 'Informe sua senha.',
  }),
});

type LoginForm = z.infer<typeof loginFormSchema>;

const defaultValues: Partial<LoginForm> = {
  documentNumber: '',
  password: '',
};

export default function LoginForm() {
  const loginCallback = useStore(modalsOpeningStore, (state) => state.loginCallback);
  const { handleJump } = useProgressiveModal<StepKeys>();
  const { startLoading, stopLoading, isLoading } = useLoading();
  const { htmlElRef, setFocus } = useFocus();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>, change: (value: string) => void) => {
    change(e.target.value);

    if (cpfValidator(e.target.value)) {
      setLoginDocumentNumber(e.target.value);
      try {
        startLoading();

        const formData: CreateAccountForm = {
          documentNumber: e.target.value,
          password: '',
          email: '',
          cellphone: '',
          legalName: '',
          birthdate: '',
          city: '',
          socialName: '',
        };

        generateRegistrationCookies({
          formData,
          maskedEmail: '',
          message: '',
          token: '',
        });

        handleJump({ key: StepKeys.REGISTER });
      } finally {
        stopLoading();
      }
    }
  };

  const handleForgotPassword = () => {
    handleJump({ key: StepKeys.FORGOT_PASSWORD });
  };

  const onSubmit = form.handleSubmit(async ({ documentNumber, password }) => {
    try {
      startLoading();

      const isPendingEmailValidationResponse = await isPendingEmailValidation(documentNumber, password);
      if (
        isPendingEmailValidationResponse
        && isPendingEmailValidationResponse.isPendingValidation
        && !isPendingEmailValidationResponse.isProfessional
      ) {
        const formData: CreateAccountForm = {
          documentNumber,
          password,
          email: isPendingEmailValidationResponse?.customer?.email,
          cellphone: isPendingEmailValidationResponse?.customer?.phone,
          legalName: isPendingEmailValidationResponse?.customer?.name,
          socialName: isPendingEmailValidationResponse?.customer?.socialName || '',
          birthdate: isPendingEmailValidationResponse?.customer?.birthday
            ? formatISO8601toBRDate(isPendingEmailValidationResponse.customer.birthday)
            : '',
          city: `${isPendingEmailValidationResponse?.customer?.location?.city} - ${isPendingEmailValidationResponse?.customer?.location?.state}`,
        };

        generateRegistrationCookies({
          formData,
          maskedEmail: isPendingEmailValidationResponse.maskedEmail,
          message: '',
          hideSocialName: true,
          token: isPendingEmailValidationResponse.token,
        });

        handleJump({ key: StepKeys.PIN_CONFIRMATION });
        return;
      }
      const loginResponse = await login(documentNumber, password);

      if (loginResponse.status || loginResponse.status === 401 || !isPendingEmailValidationResponse) {
        form.setError('password', {
          type: 'manual',
          message: 'Senha incorreta',
        });

        throw new Error('Erro ao fazer login');
      }

      setOpenSignInModal(false);
      // const { access_token } = loginResponse;
      // const { refresh_token } = loginResponse;

      // await setAuthCookies({ access_token, refresh_token });
      await resolveAuthenticatedUser();
      loginCallback?.();
    } catch (error) {
      if ((error as { status?: number })?.status === 401) {
        form.setError('password', {
          type: 'manual',
          message: 'Senha incorreta',
        });
      }
    } finally {
      stopLoading();
    }
  });

  useEffect(() => {
    setFocus();
  }, []);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} aria-label="form">
        <FormField
          control={form.control}
          name="documentNumber"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel className="text-gray-900 font-semibold">CPF</FormLabel>
              <FormControl>
                <Input
                  placeholder="000.000.000-00"
                  mask="999.999.999-99"
                  {...field}
                  inputMode="numeric"
                  ref={htmlElRef}
                  onChange={(e) => handleInputChange(e, field.onChange)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="mt-6">
              <FormLabel className="text-gray-900 font-semibold">Senha</FormLabel>
              <FormControl>
                <Input
                  startIcon={faLockKeyhole}
                  autoFocus
                  placeholder="Digite a sua senha"
                  type="password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="button"
          color="primary"
          variant="text"
          className="mt-4 justify-start"
          onClick={handleForgotPassword}
        >
          Esqueci minha senha
        </Button>

        <Button
          isLoading={isLoading}
          type="submit"
          color="primary"
          variant="default"
          className="mt-6 w-full h-12"
        >
          Entrar
        </Button>
      </form>
    </Form>
  );
}
